import { Component, OnInit } from "@angular/core";
import { Router } from "@angular/router";
import {
  mtsCanView,
  mtsDarkMode,
  setMtsDarkMode,
  clampMtsRange,
  MTS_MAX_RANGE_DAYS,
} from "./mts-theme";
import { timeout } from "rxjs/operators";
import * as moment from "moment";
import { AppService } from "src/_services/app.service";

// How long to wait for the report before giving up.
//
// This is a guard, not a tuning knob. The shared HTTP interceptor
// (_helpers/http-request.interceptor.ts) returns the raw error object from its
// catchError instead of an observable, which breaks the stream: on any failed
// request NEITHER the success nor the error callback below is ever called, and
// without this the page would sit on "Loading…" for ever with nothing said.
// Fixing that interceptor would change error handling for every page in the
// app, so it is reported rather than altered here.
const REQUEST_TIMEOUT_MS = 30000;

// One row of the summary. Money is already in major units - the API divides by
// MoneyScale once, at its own boundary, so nothing here converts anything.
interface MtsRow {
  day: number;
  date: string;

  acceptedBets: number;
  acceptedTurnover: number;
  rejectedBets: number;
  rejectedTurnover: number;
  cancelledBets: number;
  cancelledTurnover: number;
  rejectedPct: number;
  stake: number;
  averageStake: number;

  settledBets: number;
  settledTurnover: number;
  won: number;
  lost: number;
  winRatePct: number;
  // VOIDED, not cancelled - bets the settlement pipeline ended without an
  // outcome. Served and carried here, but deliberately not a column - see the
  // note under COLUMNS.
  voided: number;
  voidedTurnover: number;
  cashOuts: number;
  cashOutAmount: number;

  pnl: number;
  commission: number;

  // marginPct is P&L over ACCEPTED turnover - the figure an MTS console shows,
  // and the only margin this page displays. marginOnSettled divides by SETTLED
  // turnover and is still served; it is carried here so the shape matches what
  // the API sends, not because anything renders it.
  marginPct: number;
  marginOnSettled: number;

  unsettledBets: number;
  unsettledTurnover: number;
}

// A column is declared once, here, and both the header and every cell are
// rendered from it. Two parallel lists - one of headings, one of cells - drift
// the first time somebody inserts a column into only one of them, and the
// symptom is a whole table of numbers under the wrong headings.
interface Column {
  key: keyof MtsRow;
  label: string;
  // money | int | pct, which decides the formatting and the alignment.
  kind: "money" | "int" | "pct";
  // Colour a positive value green and a negative one red. Only for figures
  // where the sign is the point - the P&L and the margin - never for a count.
  // Those two are reported from the BOOK's side, so green is a period the
  // house won, which is what makes the colouring mean anything.
  signed?: boolean;
  // Shown on hover, for the columns whose definition is not self-evident.
  hint?: string;
}

// THE ACCEPTANCE SIDE, plus what was refused or pulled back. One list, and a
// short one on purpose.
//
// The settlement measures - settled bets and turnover, won, lost, win rate,
// cash-outs and the margin over settled turnover - are all SERVED, and the
// backend goes on storing them. They are simply not shown here. This page
// answers what the book TOOK in a period and what it has not yet resolved;
// adding the outcome columns beside that doubles the width to answer a
// different question nobody opened this page for.
//
// There used to be an Accepted/Settled toggle with a column list each. When
// the settled slot was removed the two lists were merged into one, which was
// wrong in the other direction - it put every settlement column on a page that
// had never shown them. This is the accepted list it always had, with the
// rejection and cancellation columns added.
const COLUMNS: Column[] = [
  { key: "acceptedBets", label: "Accepted Bets", kind: "int" },
  {
    key: "acceptedTurnover",
    label: "Accepted Turnover",
    kind: "money",
    hint:
      "Money at risk: the stake for a BACK, the liability for a LAY. Not the same as Stake.",
  },
  {
    key: "stake",
    label: "Stake",
    kind: "money",
    hint: "What was actually staked. Differs from Accepted Turnover whenever there are LAY bets.",
  },
  {
    key: "averageStake",
    label: "Avg Stake",
    kind: "money",
    hint: "Stake over accepted bets - not turnover over bets, which would be the average exposure.",
  },
  {
    key: "rejectedBets",
    label: "Rejected",
    kind: "int",
    hint:
      "Bets the book refused. They never existed - nothing was staked and nothing was exposed - so they are counted apart from everything else here.",
  },
  {
    key: "rejectedPct",
    label: "Rejected %",
    kind: "pct",
    hint:
      "Rejections over everything ATTEMPTED - accepted plus rejected. Not over accepted alone, or a period that refused everything would read as infinite instead of 100%.",
  },
  {
    key: "rejectedTurnover",
    label: "Rejected Turnover",
    kind: "money",
    hint: "What those bets would have put at risk, measured the same way Accepted Turnover is.",
  },
  {
    key: "cancelledBets",
    label: "Cancelled",
    kind: "int",
    hint:
      "Bets that WERE placed and then pulled back, nearly always because the operator's wallet failed afterwards. Not the same as rejected, and not the same as voided.",
  },
  {
    key: "cancelledTurnover",
    label: "Cancelled Turnover",
    kind: "money",
    hint:
      "What those bets had put at risk before they were pulled back. It is NOT part of Accepted Turnover any more, which is why it reads separately.",
  },
  {
    key: "unsettledBets",
    label: "Unsettled Bets",
    kind: "int",
    hint: "Accepted minus settled: what the period took that has not resolved yet.",
  },
  {
    key: "unsettledTurnover",
    label: "Unsettled Turnover",
    kind: "money",
    hint:
      "Accepted turnover less what settled AND what was voided - a void consumes turnover and never returns it.",
  },
  {
    key: "pnl",
    label: "P&L",
    kind: "money",
    signed: true,
    hint: "The BOOK's, not the player's: positive means the house is up.",
  },
  {
    key: "marginPct",
    label: "Margin %",
    kind: "pct",
    signed: true,
    hint:
      "P&L over ACCEPTED turnover, from the book's side - the same figure, and now the same sign, as an MTS console.",
  },
];

// NO Voided column, deliberately.
//
// What the reporting path stores is VOIDS - bets the settlement pipeline ended
// without an outcome. A cancel in the sense the business means it comes from a
// source this path does not read and is not stored yet. The two are different
// numbers, so a "Cancelled" heading over the void figures would be a wrong
// answer presented as a right one.
//
// The voids are still served (row.voided / row.voidedTurnover) and still feed
// Unsettled Turnover. When there is a real cancel to show, it gets its own
// column and the voids can get one beside it.

@Component({
  selector: "app-mts-reporting-summary",
  templateUrl: "./mts-reporting-summary.component.html",
  styleUrls: ["./mts-reporting-summary.component.scss"],
})
export class MtsReportingSummaryComponent implements OnInit {

  selected: any = {
    startDate: moment().subtract(6, "days").startOf("day"),
    endDate: moment().endOf("day"),
  };
  rangeNote = "";

  ranges: any = {
    Today: [moment().startOf("day"), moment().endOf("day")],
    Yesterday: [
      moment().subtract(1, "days").startOf("day"),
      moment().subtract(1, "days").endOf("day"),
    ],
    "Last 7 Days": [moment().subtract(6, "days").startOf("day"), moment().endOf("day")],
    // NO 30-day or This Month preset. The API refuses a range longer than 7
    // days, because the cost of a report is the number of rows in it and a
    // month of them is between 6 and 25 seconds against a 15-second read
    // timeout. Offering a preset the API will reject means handing someone an
    // error for picking what the page put in front of them.
  };

  // Operators to choose from. Empty until the list loads, and empty for a role
  // whose list cannot be fetched - the filter then simply offers nothing to
  // pick, which leaves the report across every operator, exactly as it starts.
  //
  // The objects are kept whole rather than flattened to ids, because
  // ng-multiselect-dropdown addresses its items by idField/textField.
  // SAPAdmin and nothing else, matching the API exactly.
  //
  // The endpoint is in spAPIList, which IsApplicable grants to role ==
  // "SAPAdmin" alone - so IsSAPAdmin() is the wrong test here: roleTypeSap is
  // ["SAPAdmin", "SAPUser", "RiskAdmin"], and the other two would be shown a
  // page that can only answer them with an authorisation error.
  //
  // The sidebar already hides the link, but no route in this app carries a
  // role, so a typed URL reaches the component regardless. This is what makes
  // it say so plainly instead of rendering an empty report.
  // mtsCanView(), not a raw JSON.parse of sessionStorage.
  //
  // This is a class-field initializer, so a throw here aborts the constructor
  // and the component never renders - a blank page, not a refusal message. Two
  // inputs throw: a malformed currentUser (JSON.parse("") is a SyntaxError),
  // and the sessionStorage access itself, which throws outright in a browser
  // with site data blocked. mtsCanView handles both; it lives in this
  // component's own theme file and both sibling pages already use it.
  canView = mtsCanView();
  checked = false;

  operatorData: any[] = [];
  selectedOperators: any[] = [];

  // Multi-select with a search box, the same control every other operator
  // filter in this app uses. itemsShowLimit keeps a long selection from
  // pushing the rest of the filter row off the line.
  operatorSettings = {
    singleSelection: false,
    idField: "operator_id",
    textField: "operator_id",
    selectAllText: "Select All",
    unSelectAllText: "UnSelect All",
    itemsShowLimit: 2,
    allowSearchFilter: true,
  };

  // REQUIRED by the API. Exchange and SportsBook do not share a sport id
  // space, so a report spanning both counts the same sport twice under two
  // ids. The day drill-down carries this through to keep them consistent.
  groupProvider: "Exchange" | "SportsBook" = "SportsBook";

  setGroupProvider(g: "Exchange" | "SportsBook") {
    if (this.groupProvider === g) {
      return;
    }
    this.groupProvider = g;
    this.load();
  }

  rows: MtsRow[] = [];
  totals: MtsRow = null;
  loading = false;
  errorMessage = "";

  // Remembered across both MTS pages and across reloads - see mts-theme.ts.
  dark = mtsDarkMode();

  toggleTheme() {
    this.dark = !this.dark;
    setMtsDarkMode(this.dark);
  }

  constructor(private appService: AppService, private router: Router) {}

  // The day drill-down carries the filters the summary is showing, so it is a
  // breakdown of the same book rather than of everything. They go in the URL
  // so the page survives a reload and can be shared.
  openDay(row: MtsRow) {
    const operators = (this.selectedOperators || [])
      .map((o) => (o && o.operator_id) || "")
      .filter((id) => !!id);
    this.router.navigate(["/mts/day", row.day], {
      queryParams: {
        groupProvider: this.groupProvider,
        operators: operators.length ? operators.join(",") : null,
      },
    });
  }

  ngOnInit() {
    // Re-read, like the sibling pages. The field is a snapshot taken when the
    // component was CONSTRUCTED, which on a cold load of this URL can be
    // before the session has been written - and a snapshot taken a moment too
    // early reads as "not permitted" for a user who is.
    this.canView = mtsCanView();
    this.checked = true;
    if (!this.canView) {
      return;
    }
    this.loadOperators();
    this.load();
  }

  // The list is a filter control, so failing to fetch it must not stop the
  // report from rendering: it falls back to "All operators" and the page
  // works, rather than showing an error about a dropdown.
  loadOperators() {
    const req = this.appService.getOperatorId();
    if (!req) {
      // getOperatorId returns undefined for roles that may not list
      // operators, and calling .subscribe on that would break the page.
      return;
    }
    req.subscribe(
      (res) => {
        if (!res || res.status !== "RS_OK" || !Array.isArray(res.operators)) {
          return;
        }
        this.operatorData = res.operators
          .filter((o: any) => o && o.operator_id && o.operator_id !== "all")
          .sort((a: any, b: any) => a.operator_id.localeCompare(b.operator_id));
      },
      () => {
        // Leave the list empty; the report itself is unaffected.
      }
    );
  }

  // Every one of the control's four events means the same thing here - the
  // selection changed, so the report is stale.
  onOperatorChange() {
    this.load();
  }

  // The CSV uses this too, so a download matches the table.
  get visibleColumns(): Column[] {
    return COLUMNS;
  }

  onDateChangeHandler(ev: any) {
    if (!ev || !ev.startDate || !ev.endDate) {
      return;
    }
    this.selected = clampMtsRange(ev.startDate, ev.endDate);
    this.rangeNote =
      this.selected.startDate.isSame(moment(ev.startDate), "day")
        ? ""
        : `Ranges are limited to ${MTS_MAX_RANGE_DAYS} days - showing the last ${MTS_MAX_RANGE_DAYS}.`;
    this.load();
  }

  // Each load takes a ticket; only the newest one is allowed to write to the
  // page.
  //
  // Changing operator or date fires a fresh request without cancelling the one
  // in flight, and these aggregations take seconds, so a slow reply for the
  // OLD filter can land after a fast reply for the new one - leaving the
  // previous range's numbers under the current range's heading, with nothing
  // on screen to say so. Discarding stale replies is three lines; cancelling
  // the request properly is an RxJS refactor of every load on these pages.
  private loadSeq = 0;

  load() {
    const seq = ++this.loadSeq;
    this.loading = true;
    this.errorMessage = "";

    const payload: any = {
      startTime: Math.floor(new Date(this.selected.startDate).getTime() / 1000),
      endTime: Math.floor(new Date(this.selected.endDate).getTime() / 1000),
      groupProvider: this.groupProvider,
    };
    // Nothing selected means every operator, so an empty list is left off
    // rather than sent as a filter that matches nothing.
    const operatorIds = (this.selectedOperators || [])
      .map((o) => (o && o.operator_id) || "")
      .filter((id) => !!id);
    if (operatorIds.length) {
      payload.operatorIds = operatorIds;
    }

    this.appService
      .getMtsReportingSummary(payload)
      .pipe(timeout(REQUEST_TIMEOUT_MS))
      .subscribe(
      (res) => {
        if (seq !== this.loadSeq) {
          return; // a newer load started; this reply is for the old filter
        }
        this.loading = false;
        if (res && res.status === "RS_OK") {
          this.rows = res.rows || [];
          this.totals = res.totals || null;
          return;
        }
        // An error must empty the table, never leave the previous range's
        // numbers on screen under the new range's heading.
        this.rows = [];
        this.totals = null;
        this.errorMessage = (res && res.errorDescription) || "The report could not be loaded.";
      },
      () => {
        if (seq !== this.loadSeq) {
          return;
        }
        this.loading = false;
        this.rows = [];
        this.totals = null;
        this.errorMessage = "The report could not be loaded.";
      }
    );
  }

  value(row: MtsRow, col: Column): number {
    const v = row[col.key];
    return typeof v === "number" ? v : 0;
  }

  format(row: MtsRow, col: Column): string {
    const v = this.value(row, col);
    if (col.kind === "int") {
      return v.toLocaleString("en-IN");
    }
    if (col.kind === "pct") {
      return v.toFixed(2) + "%";
    }
    return v.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  signClass(row: MtsRow, col: Column): string {
    if (!col.signed) {
      return "";
    }
    const v = this.value(row, col);
    if (v > 0) {
      return "pos";
    }
    if (v < 0) {
      return "neg";
    }
    return "";
  }

  downloadCsv() {
    const cols = this.visibleColumns;
    const head = ["Date", ...cols.map((c) => c.label)];
    const lines = [head.join(",")];
    for (const r of this.rows) {
      lines.push([r.date, ...cols.map((c) => this.value(r, c))].join(","));
    }
    if (this.totals) {
      lines.push(["Total", ...cols.map((c) => this.value(this.totals, c))].join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `mts-reporting-summary-${moment(this.selected.startDate).format(
      "YYYYMMDD"
    )}-${moment(this.selected.endDate).format("YYYYMMDD")}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }
}
